/**
 * Establishment picker for create/edit forms, optionally filtered by department.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, useCallback } from 'react';
import { ASSIGNMENT_SCOPE_PAGE_SIZE, getEstablishments } from '@/services/establishments.service';
import { useAuthStore } from '@/stores/authStore';
import { useConnectedUser } from '@/hooks/useConnectedUser';
import { formatEstablishmentSelectLabel } from '@/utils/format-establishment-select-label-utils';
import type { SelectOption } from '@/components/forms/Select';
import type { EstablishmentAssignmentScopeMode } from '@/types/establishment.types';

/** `GET /establishments` for create/edit pickers; department filters the list. */
export function useActorEstablishmentAssignmentScope(options?: {
  departmentCode?: string;
  /** When true, only `prospection_status = valide`. Remote student pickers pass false. */
  validatedProspectionOnly?: boolean;
  /** When true, skip the automatic list until `departmentCode` is set; typed search still runs. */
  requireDepartment?: boolean;
}) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { limitEstablishmentPickerToValidated } = useConnectedUser();
  const [searchQuery, setSearchQuery] = useState('');

  const departmentCode = options?.departmentCode?.trim() || undefined;
  const validatedOnly = options?.validatedProspectionOnly ?? limitEstablishmentPickerToValidated;
  const canFetchInitial = isAuthenticated && (!options?.requireDepartment || !!departmentCode);

  const listParams = {
    page: 1,
    pageSize: ASSIGNMENT_SCOPE_PAGE_SIZE,
    ...(validatedOnly ? { isProspectionValidated: true as const } : {}),
    ...(departmentCode ? { departments: [departmentCode] } : {}),
  };

  const {
    data: initialData,
    isLoading: initialLoading,
    isError,
  } = useQuery({
    queryKey: [
      'establishments',
      'assignment-scope',
      'initial',
      validatedOnly,
      departmentCode ?? null,
    ],
    queryFn: () => getEstablishments(listParams),
    enabled: canFetchInitial,
  });

  const total = initialData?.total ?? 0;
  const isSingleLocked = total === 1 && (initialData?.data.length ?? 0) === 1;
  const trimmedSearch = searchQuery.trim();

  const { data: searchData, isFetching: searchFetching } = useQuery({
    queryKey: [
      'establishments',
      'assignment-scope',
      'search',
      trimmedSearch,
      validatedOnly,
      departmentCode ?? null,
    ],
    queryFn: () =>
      getEstablishments({
        ...listParams,
        search: trimmedSearch,
      }),
    enabled: isAuthenticated && !isSingleLocked && trimmedSearch.length > 0,
  });

  const activePage = trimmedSearch.length > 0 ? searchData : initialData;
  const establishments = activePage?.data ?? [];
  const isLoading = initialLoading;
  const isSearchLoading = searchFetching;
  const isReady = !initialLoading && !!initialData;
  const mode: EstablishmentAssignmentScopeMode = 'restricted';

  const selectOptions: SelectOption[] = useMemo(
    () =>
      establishments.map((establishment) => ({
        value: establishment.id,
        label: formatEstablishmentSelectLabel(establishment),
      })),
    [establishments],
  );

  const singleEstablishment = isSingleLocked ? initialData!.data[0] : undefined;
  const singleEstablishmentLabel = singleEstablishment
    ? formatEstablishmentSelectLabel(singleEstablishment)
    : undefined;

  const onSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const validatedSuffix = validatedOnly ? ' validés' : '';
  const paginatedHelperText = `Recherche parmi vos ${total} établissements${validatedSuffix} (${ASSIGNMENT_SCOPE_PAGE_SIZE} résultats affichés)`;
  const restrictedHelperText = validatedOnly
    ? 'Recherche limitée aux établissements au statut de prospection Validé'
    : 'Recherche limitée à vos établissements assignés';
  const helperText =
    total > ASSIGNMENT_SCOPE_PAGE_SIZE ? paginatedHelperText : restrictedHelperText;

  return {
    mode,
    total,
    establishments,
    selectOptions,
    restrictToEstablishmentIds: establishments.map((e) => e.id),
    restrictToEstablishments: establishments,
    isSingleLocked,
    singleEstablishment,
    singleEstablishmentLabel,
    isLoading,
    isSearchLoading,
    isError,
    isReady,
    onSearch,
    searchQuery,
    helperText,
  };
}
